import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { t } from "@/i18n";
import { toast } from "sonner";
import { Hourglass, ArrowRight } from "lucide-react";

export default function Lounge() {
  const [waiting, setWaiting] = useState([]);
  const [tables, setTables] = useState([]);
  const [transferTarget, setTransferTarget] = useState(null);
  const today = new Date().toISOString().slice(0, 10);

  const load = async () => {
    const [r1, r2] = await Promise.all([
      api.get("/reservations", { params: { date: today, status: "waiting_lounge" } }),
      api.get("/tables"),
    ]);
    setWaiting(r1.data);
    setTables(r2.data);
  };
  useEffect(() => { load(); }, []);  // eslint-disable-line

  const transfer = async (reservationId, tableId) => {
    try {
      await api.patch(`/reservations/${reservationId}`, { status: "seated", table_id: tableId });
      toast.success("Gast platziert");
      setTransferTarget(null);
      load();
    } catch {
      toast.error("Fehler beim Platzieren");
    }
  };

  const elapsed = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const mins = Math.max(0, Math.round((Date.now() - d.getTime()) / 60000));
    return `${mins} Min.`;
  };

  const freeTables = tables.filter((t) => t.status === "free");

  return (
    <div className="px-6 lg:px-10 py-10 max-w-7xl mx-auto">
      <div className="mb-10">
        <h1 className="font-editorial italic text-5xl sm:text-6xl leading-[1.05] tracking-tight text-[var(--text-primary)]" data-testid="lounge-title">{t("lounge.title")}</h1>
      </div>

      {waiting.length === 0 ? (
        <div className="surface-1 rounded-md p-16 text-center" data-testid="lounge-empty">
          <Hourglass className="w-8 h-8 text-gold/40 mx-auto mb-4" strokeWidth={1} />
          <p className="text-sm text-[var(--text-muted)]">{t("lounge.no_waiting")}</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {waiting.map((r) => (
            <div key={r.reservation_id} data-testid={`lounge-${r.reservation_id}`} className="surface-1 rounded-md p-6 border-l-2 border-gold relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-gold/5 to-transparent pointer-events-none" />
              <div className="relative">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="font-heading text-lg">{r.guest_name}</div>
                    <div className="text-xs text-[var(--text-muted)]">{r.party_size} {t("common.guests")}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gold font-heading">{elapsed(r.waiting_since)}</div>
                    <div className="text-[10px] text-[var(--text-dim)] uppercase tracking-wider">{t("lounge.waiting_since")}</div>
                  </div>
                </div>
                {r.notes && <div className="text-xs italic text-[var(--text-muted)] mb-4">„{r.notes}"</div>}
                <button
                  onClick={() => setTransferTarget(r)}
                  data-testid={`lounge-transfer-${r.reservation_id}`}
                  className="w-full btn-primary text-xs inline-flex items-center justify-center gap-2"
                >
                  {t("lounge.transfer")} <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {transferTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-6" onClick={() => setTransferTarget(null)}>
          <div className="surface-1 rounded-md w-full max-w-lg p-8" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-xl font-light mb-2">{transferTarget.guest_name} platzieren</h3>
            <p className="text-xs text-[var(--text-muted)] mb-6">Freien Tisch auswählen</p>
            <div className="grid grid-cols-3 gap-2 max-h-80 overflow-y-auto">
              {freeTables.length === 0 && (
                <div className="col-span-3 text-center text-sm text-[var(--text-dim)] py-8">Keine freien Tische verfügbar.</div>
              )}
              {freeTables.map((tbl) => (
                <button
                  key={tbl.table_id}
                  onClick={() => transfer(transferTarget.reservation_id, tbl.table_id)}
                  data-testid={`lounge-pick-table-${tbl.table_id}`}
                  className="surface-2 rounded-md p-3 hover:border-gold/40 hover:bg-gold/5 transition-all text-center"
                >
                  <div className="font-heading text-lg">{tbl.name}</div>
                  <div className="text-[10px] text-[var(--text-muted)]">{tbl.seats} Plätze</div>
                </button>
              ))}
            </div>
            <button onClick={() => setTransferTarget(null)} className="btn-ghost text-xs w-full mt-6">{t("common.cancel")}</button>
          </div>
        </div>
      )}
    </div>
  );
}
